import React, { useState, useEffect } from 'react';
import { PlusIcon, PencilSquareIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { Racer, RacerStats, SocialMedia, TradingCardConfig } from '../../types/racer';
import { api } from '../../services/api';
import { Modal } from '../../components/common/Modal';

interface RacerFormData {
  name: string;
  bio: string;
  imageUrl: string;
  team: string;
  rank: number;
  stats: RacerStats;
  achievements: string[];
  carNumber: string;
  socialMedia: SocialMedia;
  isActive: boolean;
  tradingCardConfig: TradingCardConfig;
}

const defaultStats: RacerStats = {
  wins: 0,
  podiums: 0,
  races: 0,
  averageSpeed: 0,
  bestFinish: 0,
  championships: 0,
  topTenFinishes: 0,
  classWins: {},
  personalBests: {},
  progression: {}
};

const defaultSocialMedia: SocialMedia = {
  instagram: '',
  twitter: '',
  tiktok: '',
  website: '',
};

const defaultTradingCardConfig: TradingCardConfig = {
  backgroundColor: '#1a1a1a',
  textColor: '#ffffff',
  fontFamily: 'Helvetica Neue',
  cardStyle: 'modern',
};

const RacerFormContent: React.FC<{
  formData: RacerFormData;
  setFormData: React.Dispatch<React.SetStateAction<RacerFormData>>;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  editingRacer: Racer | null;
  onClose: () => void;
}> = ({ formData, setFormData, handleSubmit, editingRacer, onClose }) => {
  const handleAddPersonalBest = () => {
    const newBests = {
      ...formData.stats.personalBests,
      'New Track': {
        time: '00:00:00',
        speed: 0,
        date: new Date(),
        position: 0
      }
    };
    setFormData({
      ...formData,
      stats: { ...formData.stats, personalBests: newBests },
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Information */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <h4 className="font-semibold text-gray-900 mb-4">Basic Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-deep text-gray-900"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Team</label>
            <input
              type="text"
              value={formData.team}
              onChange={(e) => setFormData({ ...formData, team: e.target.value })}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-deep text-gray-900"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Car Number</label>
            <input
              type="text"
              value={formData.carNumber}
              onChange={(e) => setFormData({ ...formData, carNumber: e.target.value })}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-deep text-gray-900"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Rank</label>
            <input
              type="number"
              value={formData.rank}
              onChange={(e) => setFormData({ ...formData, rank: parseInt(e.target.value) })}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-deep text-gray-900"
              required
            />
          </div>
        </div>
      </div>

      {/* Bio and Image */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <h4 className="font-semibold text-gray-900 mb-4">Profile</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Bio</label>
            <textarea
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              rows={4}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-deep text-gray-900"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Image URL</label>
            <input
              type="url"
              value={formData.imageUrl}
              onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-deep text-gray-900"
              required
            />
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <h4 className="font-semibold text-gray-900 mb-4">Racing Statistics</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Wins</label>
            <input
              type="number"
              value={formData.stats.wins}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  stats: { ...formData.stats, wins: parseInt(e.target.value) },
                })
              }
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-deep text-gray-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Podiums</label>
            <input
              type="number"
              value={formData.stats.podiums}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  stats: { ...formData.stats, podiums: parseInt(e.target.value) },
                })
              }
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-deep text-gray-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Races</label>
            <input
              type="number"
              value={formData.stats.races}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  stats: { ...formData.stats, races: parseInt(e.target.value) },
                })
              }
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-deep text-gray-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Average Speed</label>
            <input
              type="number"
              value={formData.stats.averageSpeed}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  stats: { ...formData.stats, averageSpeed: parseFloat(e.target.value) },
                })
              }
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-deep text-gray-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Best Finish</label>
            <input
              type="number"
              value={formData.stats.bestFinish}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  stats: { ...formData.stats, bestFinish: parseInt(e.target.value) },
                })
              }
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-deep text-gray-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Championships</label>
            <input
              type="number"
              value={formData.stats.championships}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  stats: { ...formData.stats, championships: parseInt(e.target.value) },
                })
              }
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-deep text-gray-900"
            />
          </div>
        </div>
      </div>

      {/* Class Wins */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <h4 className="font-semibold text-gray-900 mb-4">Class Wins</h4>
        <div className="space-y-4">
          {Object.entries(formData.stats.classWins).map(([className, wins], index) => (
            <div key={index} className="flex items-center gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-900 mb-1">Class Name</label>
                <input
                  type="text"
                  value={className}
                  onChange={(e) => {
                    const newClassWins = { ...formData.stats.classWins };
                    delete newClassWins[className];
                    newClassWins[e.target.value] = wins;
                    setFormData({
                      ...formData,
                      stats: { ...formData.stats, classWins: newClassWins },
                    });
                  }}
                  className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-deep text-gray-900"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-900 mb-1">Wins</label>
                <input
                  type="number"
                  value={wins}
                  onChange={(e) => {
                    const newClassWins = { ...formData.stats.classWins };
                    newClassWins[className] = parseInt(e.target.value);
                    setFormData({
                      ...formData,
                      stats: { ...formData.stats, classWins: newClassWins },
                    });
                  }}
                  className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-deep text-gray-900"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  const newClassWins = { ...formData.stats.classWins };
                  delete newClassWins[className];
                  setFormData({
                    ...formData,
                    stats: { ...formData.stats, classWins: newClassWins },
                  });
                }}
                className="mt-6 p-2 text-red-600 hover:text-red-800"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => {
              const newClassWins = { ...formData.stats.classWins, 'New Class': 0 };
              setFormData({
                ...formData,
                stats: { ...formData.stats, classWins: newClassWins },
              });
            }}
            className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Add Class
          </button>
        </div>
      </div>

      {/* Personal Bests */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <h4 className="font-semibold text-gray-900 mb-4">Personal Bests</h4>
        <div className="space-y-4">
          {Object.entries(formData.stats.personalBests).map(([track, best], index) => (
            <div key={index} className="space-y-2 border-b pb-4">
              <div className="flex items-center justify-between">
                <h5 className="font-medium text-gray-900">{track}</h5>
                <button
                  type="button"
                  onClick={() => {
                    const newBests = { ...formData.stats.personalBests };
                    delete newBests[track];
                    setFormData({
                      ...formData,
                      stats: { ...formData.stats, personalBests: newBests },
                    });
                  }}
                  className="text-red-600 hover:text-red-800"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-1">Time</label>
                  <input
                    type="text"
                    value={best.time}
                    onChange={(e) => {
                      const newBests = { ...formData.stats.personalBests };
                      newBests[track] = { ...best, time: e.target.value };
                      setFormData({
                        ...formData,
                        stats: { ...formData.stats, personalBests: newBests },
                      });
                    }}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-deep text-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-1">Speed</label>
                  <input
                    type="number"
                    value={best.speed}
                    onChange={(e) => {
                      const newBests = { ...formData.stats.personalBests };
                      newBests[track] = { ...best, speed: parseFloat(e.target.value) };
                      setFormData({
                        ...formData,
                        stats: { ...formData.stats, personalBests: newBests },
                      });
                    }}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-deep text-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-1">Date</label>
                  <input
                    type="date"
                    value={new Date(best.date).toISOString().split('T')[0]}
                    onChange={(e) => {
                      const newBests = { ...formData.stats.personalBests };
                      newBests[track] = { ...best, date: new Date(e.target.value) };
                      setFormData({
                        ...formData,
                        stats: { ...formData.stats, personalBests: newBests },
                      });
                    }}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-deep text-gray-900"
                  />
                </div>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={handleAddPersonalBest}
            className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Add Track Record
          </button>
        </div>
      </div>

      {/* Progression Stats */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <h4 className="font-semibold text-gray-900 mb-4">Year-by-Year Progression</h4>
        <div className="space-y-4">
          {Object.entries(formData.stats.progression).map(([year, stats], index) => (
            <div key={index} className="space-y-2 border-b pb-4">
              <div className="flex items-center justify-between">
                <h5 className="font-medium text-gray-900">{year}</h5>
                <button
                  type="button"
                  onClick={() => {
                    const newProgression = { ...formData.stats.progression };
                    delete newProgression[year];
                    setFormData({
                      ...formData,
                      stats: { ...formData.stats, progression: newProgression },
                    });
                  }}
                  className="text-red-600 hover:text-red-800"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-1">Average Position</label>
                  <input
                    type="number"
                    value={stats.averagePosition}
                    onChange={(e) => {
                      const newProgression = { ...formData.stats.progression };
                      newProgression[year] = { ...stats, averagePosition: parseFloat(e.target.value) };
                      setFormData({
                        ...formData,
                        stats: { ...formData.stats, progression: newProgression },
                      });
                    }}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-deep text-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-1">Average Speed</label>
                  <input
                    type="number"
                    value={stats.averageSpeed}
                    onChange={(e) => {
                      const newProgression = { ...formData.stats.progression };
                      newProgression[year] = { ...stats, averageSpeed: parseFloat(e.target.value) };
                      setFormData({
                        ...formData,
                        stats: { ...formData.stats, progression: newProgression },
                      });
                    }}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-deep text-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-1">Total Races</label>
                  <input
                    type="number"
                    value={stats.totalRaces}
                    onChange={(e) => {
                      const newProgression = { ...formData.stats.progression };
                      newProgression[year] = { ...stats, totalRaces: parseInt(e.target.value) };
                      setFormData({
                        ...formData,
                        stats: { ...formData.stats, progression: newProgression },
                      });
                    }}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-deep text-gray-900"
                  />
                </div>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={() => {
              const currentYear = new Date().getFullYear().toString();
              const newProgression = {
                ...formData.stats.progression,
                [currentYear]: {
                  averagePosition: 0,
                  averageSpeed: 0,
                  totalRaces: 0
                }
              };
              setFormData({
                ...formData,
                stats: { ...formData.stats, progression: newProgression },
              });
            }}
            className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Add Year
          </button>
        </div>
      </div>

      {/* Social Media */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <h4 className="font-semibold text-gray-900 mb-4">Social Media</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Instagram</label>
            <input
              type="text"
              value={formData.socialMedia.instagram}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  socialMedia: { ...formData.socialMedia, instagram: e.target.value },
                })
              }
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-deep text-gray-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Twitter</label>
            <input
              type="text"
              value={formData.socialMedia.twitter}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  socialMedia: { ...formData.socialMedia, twitter: e.target.value },
                })
              }
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-deep text-gray-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">TikTok</label>
            <input
              type="text"
              value={formData.socialMedia.tiktok}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  socialMedia: { ...formData.socialMedia, tiktok: e.target.value },
                })
              }
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-deep text-gray-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Website</label>
            <input
              type="url"
              value={formData.socialMedia.website}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  socialMedia: { ...formData.socialMedia, website: e.target.value },
                })
              }
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-deep text-gray-900"
            />
          </div>
        </div>
      </div>

      {/* Trading Card Configuration */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <h4 className="font-semibold text-gray-900 mb-4">Trading Card Configuration</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Background Color</label>
            <input
              type="color"
              value={formData.tradingCardConfig.backgroundColor}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  tradingCardConfig: {
                    ...formData.tradingCardConfig,
                    backgroundColor: e.target.value,
                  },
                })
              }
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-deep text-gray-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Text Color</label>
            <input
              type="color"
              value={formData.tradingCardConfig.textColor}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  tradingCardConfig: {
                    ...formData.tradingCardConfig,
                    textColor: e.target.value,
                  },
                })
              }
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-deep text-gray-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Font Family</label>
            <select
              value={formData.tradingCardConfig.fontFamily}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  tradingCardConfig: {
                    ...formData.tradingCardConfig,
                    fontFamily: e.target.value,
                  },
                })
              }
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-deep text-gray-900"
            >
              <option value="Helvetica Neue">Helvetica Neue</option>
              <option value="Arial">Arial</option>
              <option value="Roboto">Roboto</option>
              <option value="Inter">Inter</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Card Style</label>
            <select
              value={formData.tradingCardConfig.cardStyle}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  tradingCardConfig: {
                    ...formData.tradingCardConfig,
                    cardStyle: e.target.value,
                  },
                })
              }
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-deep text-gray-900"
            >
              <option value="modern">Modern</option>
              <option value="classic">Classic</option>
              <option value="vintage">Vintage</option>
            </select>
          </div>
        </div>
      </div>

      {/* Active Status */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <div className="flex items-center">
          <input
            type="checkbox"
            checked={formData.isActive}
            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
          />
          <label className="ml-2 block text-sm font-medium text-gray-900">
            Active Racer
          </label>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-900 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700"
        >
          {editingRacer ? 'Save Changes' : 'Add Racer'}
        </button>
      </div>
    </form>
  );
};

export const RacersManagementPage: React.FC = () => {
  const [racers, setRacers] = useState<Racer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRacer, setEditingRacer] = useState<Racer | null>(null);
  const [formData, setFormData] = useState<RacerFormData>({
    name: '',
    bio: '',
    imageUrl: '',
    team: '',
    rank: 0,
    stats: defaultStats,
    achievements: [],
    carNumber: '',
    socialMedia: defaultSocialMedia,
    isActive: true,
    tradingCardConfig: defaultTradingCardConfig,
  });

  useEffect(() => {
    fetchRacers();
  }, []);

  const fetchRacers = async () => {
    try {
      const data = await api.admin.getRacers();
      setRacers(data);
    } catch (err) {
      setError('Failed to load racers');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingRacer) {
        await api.admin.updateRacer(editingRacer.id, formData);
      } else {
        await api.admin.createRacer(formData);
      }
      await fetchRacers();
      setIsModalOpen(false);
      resetForm();
    } catch (err) {
      console.error('Failed to save racer:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this racer?')) return;

    try {
      await api.admin.deleteRacer(id);
      await fetchRacers();
    } catch (err) {
      console.error('Failed to delete racer:', err);
    }
  };

  const openEditModal = (racer: Racer) => {
    setEditingRacer(racer);
    setFormData({
      name: racer.name,
      bio: racer.bio,
      imageUrl: racer.imageUrl,
      team: racer.team,
      rank: racer.rank,
      stats: racer.stats,
      achievements: racer.achievements,
      carNumber: racer.carNumber,
      socialMedia: racer.socialMedia,
      isActive: racer.isActive,
      tradingCardConfig: racer.tradingCardConfig,
    });
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setEditingRacer(null);
    setFormData({
      name: '',
      bio: '',
      imageUrl: '',
      team: '',
      rank: 0,
      stats: defaultStats,
      achievements: [],
      carNumber: '',
      socialMedia: defaultSocialMedia,
      isActive: true,
      tradingCardConfig: defaultTradingCardConfig,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-400"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-400 text-center py-8">
        <p>{error}</p>
        <button
          onClick={fetchRacers}
          className="mt-4 px-4 py-2 bg-teal-500 text-white rounded hover:bg-teal-600"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-semibold text-teal-300">Racers</h1>
        <button
          onClick={() => {
            resetForm();
            setIsModalOpen(true);
          }}
          className="inline-flex items-center px-4 py-2 bg-teal-500 text-white rounded-md hover:bg-teal-600 transition-colors"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Racer
        </button>
      </div>

      <div className="bg-black/20 backdrop-blur-md rounded-lg border border-teal-500/20 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-600">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-teal-300 uppercase tracking-wider">
                  Racer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teal-300 uppercase tracking-wider">
                  Team
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teal-300 uppercase tracking-wider">
                  Car Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teal-300 uppercase tracking-wider">
                  Rank
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-teal-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-600">
              {racers.map((racer) => (
                <tr key={racer.id} className="hover:bg-black/40 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 flex-shrink-0">
                        <img
                          className="h-10 w-10 rounded-full object-cover"
                          src={racer.imageUrl}
                          alt={racer.name}
                        />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-200">{racer.name}</div>
                        <div className="text-sm text-gray-400">{racer.bio.substring(0, 50)}...</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-teal-900/50 text-teal-300">
                      {racer.team}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    {racer.carNumber}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    #{racer.rank}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => openEditModal(racer)}
                      className="text-teal-300 hover:text-teal-200 mr-4"
                    >
                      <PencilSquareIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(racer.id)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingRacer ? 'Edit Racer' : 'Add Racer'}
      >
        <RacerFormContent
          formData={formData}
          setFormData={setFormData}
          handleSubmit={handleSubmit}
          editingRacer={editingRacer}
          onClose={() => setIsModalOpen(false)}
        />
      </Modal>
    </div>
  );
}; 